import React, { FC, useContext, useEffect, useMemo, useState } from "react";
import { GetServerSideProps } from "next";
import Head from "next/head";
import { useRouter } from "next/router";
import unescape from "lodash/unescape";
import { CartContext } from "context/cart";
import { ZipCodeContext } from "context/zipCode";
import { ModalContext } from "context/modal";
import { AppContext } from "context/app";
import { LogContext } from "context/log";
import { UserContext } from "context/user";
import PurifiedHtml from "@pepdirect/ui/PurifiedHtml";
import { VariantDropdown } from "@pepdirect/ui/VariantDropdown";
import { Heading } from "@pepdirect/ui/heading";
import { ExternalLink } from "@pepdirect/ui/ExternalLink";
import { Expandable } from "components/Expandable";
import { QuantityDropdown } from "components/Product/QuantityDropdown";
import { Recommended } from "components/Product/Recommended";
import { Images } from "components/Product/Images";
import { DeliverToZip } from "components/Product/DeliverToZip";
import { PriceChooser } from "components/Product/PriceChooser";
import { ShippingBadge } from "components/Product/ShippingBadge";
import createSsrClient from "services/graphql/ssr-client";
import {
  ItemBySlugDocument,
  ItemBySlugQuery,
  ItemBySlugQueryVariables,
} from "services/graphql/generated";
import { logProductDetailBuyButtonClickEvent } from "@pepdirect/helpers/analyticsLogger";
import { getCloudflareImageUrl } from "@pepdirect/helpers/cloudflareImages";
import { useRestoreScrollOnBrowserBack } from "@pepdirect/helpers/useScrollRestoration";
import {
  convertFromCentsToDollars,
  formatDollars,
  FORMAT_NO_SIGN,
} from "helpers/currency";
import { isFeatureFlagEnabled } from "helpers/featureFlags";
import {
  ItemVariantOption,
  PAGE_TYPE_ENUM,
  Product,
} from "@pepdirect/shared/types";
import { META_TITLE, CANONICAL_URL_BASE } from "constants/meta";
import { Subscription_Options } from "constants/subscription";
import { endpoints, cart } from "config";
import st from "./product.module.scss";

const { ONE_TIME, SUB_SAVE } = Subscription_Options;
const { maxCartQty: maxCartQtyDefault } = cart;

interface ProductPageProps {
  product: ItemBySlugQuery["itemBySlug"] | null;
}

export const getServerSideProps: GetServerSideProps<ProductPageProps> = async ({
  params,
  ...rest
}) => {
  // Here we use server side GQL client
  const gqlClient = createSsrClient(rest.req);
  try {
    const { data } = await gqlClient.query<
      ItemBySlugQuery,
      ItemBySlugQueryVariables
    >({
      query: ItemBySlugDocument,
      variables: { slug: params?.slug as string },
    });

    const product = data?.itemBySlug;

    if (!product) {
      return {
        notFound: true,
      };
    }

    return {
      props: {
        product,
      },
    };
  } catch (e) {
    return {
      notFound: true,
    };
  }
};

const NA = "N/A";

const ProductPage: FC<ProductPageProps> = ({ product }) => {
  const { currentUserId } = useContext(UserContext);
  const { cart, updateCartItem } = useContext(CartContext);
  const { featureFlags, tenant } = useContext(AppContext);
  const { blockedItems } = useContext(ZipCodeContext);
  const { open } = useContext(ModalContext);

  // We have this as unknown as Product because currently we share useAnalyticsLogger types in lib
  const { useLogPageView } = useContext(LogContext);

  const router = useRouter();
  useRestoreScrollOnBrowserBack(router);

  const cartItem = cart?.items?.find(
    (item) => item.product.id === product?._id
  );
  const quantity = cartItem?.quantity || 0;

  const [subscriptionOption, setSubscriptionOption] =
    useState<Subscription_Options>(
      cartItem?.subscriptionIntervalInDays ? SUB_SAVE : ONE_TIME
    );

  const { discount, isSubscribable, subscriptionPrice } =
    product?.subscriptionMetadata || {};
  const formattedSubscriptionPrice =
    (subscriptionPrice && convertFromCentsToDollars(subscriptionPrice)) || null;
  const intervals = useMemo(() => {
    const intervalsVal =
      tenant?.shoppingBagConfig?.availableSubscriptionIntervalsInDays || [];
    return intervalsVal;
  }, [tenant]);
  const showPriceChooser =
    isSubscribable && tenant?.shoppingBagConfig?.orderSubscriptionsEnabled;
  const [defaultInterval] = intervals;

  const [interval, setInterval] = useState(
    cartItem?.subscriptionIntervalInDays || defaultInterval
  );

  useEffect(() => {
    const [firstInterval] = intervals;
    setInterval(cartItem?.subscriptionIntervalInDays || firstInterval);
  }, [cartItem?.subscriptionIntervalInDays, intervals]);

  useEffect(() => {
    if (cartItem?.subscriptionIntervalInDays) {
      setInterval(cartItem?.subscriptionIntervalInDays);
      setSubscriptionOption(SUB_SAVE);
    }
  }, [cartItem?.subscriptionIntervalInDays]);

  const maxCartQty =
    tenant?.shoppingBagConfig?.maxAmountForSelect || maxCartQtyDefault;
  const isItemUnavailable = blockedItems.includes(product?._id);
  const openZipCodeModal = () => open("ZIPCODE_MODAL");

  const metaTitle = `${product?.itemTitle} – by ${product?.itemDesignerArtistBrandName} – ${META_TITLE}`;
  const metaUrl = `${CANONICAL_URL_BASE}/product/${product?.slug}`;
  // TODO: move to a helper?
  const metaDescription = unescape(product?.description || "")
    .replace("<br />", " ")
    .replace(/(&nbsp;|<([^>]+)>)/gi, "")
    .slice(0, 155)
    .concat("...");
  const metaImage = getCloudflareImageUrl(
    endpoints.productsImgCdn,
    product?.imgUrl || "",
    { width: 600 }
  );
  const metaPrice = formatDollars(product?.itemPrice || 0, FORMAT_NO_SIGN);
  const price = convertFromCentsToDollars(product?.price || 0);

  const onVariantChange = ({ value }: { value: string }) => {
    if (value !== product?._id)
      router.push("/product/[slug]", `/product/${value}`);
  };
  const { collection } = (router?.query || {}) as {
    collection: string | undefined;
  };
  const pageCategory =
    collection ||
    product?.stories?.[0]?.title2 ||
    product?.stories?.[0]?.title1;
  useLogPageView(
    {
      pageType: PAGE_TYPE_ENUM.product,
      pageTitle: product?.title || "",
      pageCategory: pageCategory || undefined,
      id: product?._id || "",
    },
    product as unknown as Product
  );

  // This is a temporary adapter function to convert GQL variants into required REST logic
  // We will probably need to handle this logic in shared components more elegantly when GQL migration is done.
  const convertGQLVariantOptions = (
    options: NonNullable<ItemBySlugQuery["itemBySlug"]>["variantOptions"]
  ): ItemVariantOption[] => {
    return (
      options?.map((opt) => ({
        _id: opt?.variant?.slug || "",
        itemId: opt?.variant?.slug || "",
        variantOption: {
          _id: opt?.variantOptions?.[0]?.value || "",
          name: opt?.variantOptions?.[0]?.name || "",
          position: opt?.variantOptions?.[0]?.position || 1,
          value: opt?.variantOptions?.[0]?.value || "",
        },
      })) || []
    );
  };

  return (
    <div className={st.wrapper}>
      <Head>
        <title>{metaTitle}</title>
        <meta name="og:title" content={metaTitle} key="og:title" />
        <link rel="canonical" href={metaUrl} />
        <meta name="og:url" content={metaUrl} key="og:url" />
        <meta name="description" content={metaDescription} key="description" />
        <meta
          name="og:description"
          content={metaDescription}
          key="og:description"
        />
        <meta name="og:image" content={metaImage} key="og:image" />
        <meta name="og:price:amount" content={metaPrice} />
        <meta name="og:type" content="product" key="og:type" />
      </Head>
      <div className={st.itemWrapper}>
        <div className={st.itemImages}>
          <Images
            image={product?.imgUrl || ""}
            images={product?.imgVariantUrls || []}
            alt={`${product?.itemTitle} photo`}
            key={`images_${product?._id}`}
          />
        </div>
        <div className={st.itemData}>
          <div className={st.itemActionsWrapper}>
            <div className={st.title}>
              <Heading level="h1" size="xl">
                {product?.itemTitle}
              </Heading>
            </div>
            {isItemUnavailable ? (
              <>
                <h2 className={st.unavailableHeader}>Currently unavailable</h2>
                <p className={st.unavailableText}>
                  This product cannot be delivered to your area at this time.
                  Please{" "}
                  <span
                    className={st.modalLink}
                    role="button"
                    onClick={openZipCodeModal}
                  >
                    update your delivery location
                  </span>{" "}
                  to check availability.
                </p>
              </>
            ) : (
              <>
                {showPriceChooser && interval ? (
                  <PriceChooser
                    discount={{
                      id: discount?.id,
                      activeFrom: discount?.activeFrom,
                      amount: discount?.amount || 0,
                      isPercent: discount?.isPercent || false,
                    }}
                    intervals={intervals}
                    onIntervalChange={setInterval}
                    onSubscriptionOptionChange={setSubscriptionOption}
                    price={price}
                    selectedInterval={interval}
                    subscriptionOption={subscriptionOption}
                    subscriptionPrice={formattedSubscriptionPrice}
                  />
                ) : (
                  <span className={st.price}>Our price {price}</span>
                )}
                <VariantDropdown
                  currentId={product?._id}
                  itemVariantOptions={convertGQLVariantOptions(
                    product?.variantOptions
                  )}
                  onVariantChange={onVariantChange}
                />
                <QuantityDropdown
                  quantity={quantity}
                  onChange={async (quantity: number) => {
                    // We have this as unknown as Product because currently we share useAnalyticsLogger types in lib
                    logProductDetailBuyButtonClickEvent(
                      currentUserId,
                      product as unknown as Product,
                      quantity
                    );
                    return updateCartItem({
                      productId: product?._id,
                      quantity,
                      subscriptionIntervalInDays:
                        subscriptionOption === SUB_SAVE && interval
                          ? interval
                          : null,
                    });
                  }}
                  outOfStock={!!product?.isOutOfStock}
                  inventoryQty={product?.inventoryQty || 0}
                  minOrderQty={product?.minOrderQty || 0}
                  maxCartQty={maxCartQty}
                  key={`quantity_${product?._id}`}
                />
              </>
            )}
            {isFeatureFlagEnabled(featureFlags, "geo_location") && (
              <DeliverToZip isSubscription={!!(showPriceChooser && interval)} />
            )}
          </div>
          <div className={st.description}>
            <h2 className={st.descriptionTitle}>Overview</h2>
            <PurifiedHtml html={product?.description || ""} />
          </div>
          <Expandable
            className={st.expandable}
            title="Details"
            key={`expandable_${product?._id}`}
          >
            <div className={st.expandableContent}>
              {/* TODO Repurposed / Hacked the returns policy to display additional item details; should properly implement this feature */}
              <PurifiedHtml html={product?.returnsPolicy || ""} />
              <p>
                <strong>Brand:</strong>{" "}
                {product?.itemDesignerArtistBrandName || NA}
              </p>
              <p>
                <strong>Ingredients/Material:</strong>{" "}
                {product?.material ||
                  product?.nutritionData?.ingredientsTextEn ||
                  NA}
              </p>
              <p>
                <strong>Size/Weight:</strong> {product?.itemDimensions || NA}
              </p>
            </div>
          </Expandable>
          <div className={st.returns}>
            <h2 className={st.descriptionTitle}>Shipping and Returns</h2>
            <div>
              <ShippingBadge />
              <p>
                Delivery times may vary, especially during peak periods.
                Unfortunately, we don’t currently ship to Alaska or Hawaii.
              </p>
            </div>
            <div>
              <h3>Returns and Issues:</h3>
              <ul>
                <li>
                  All sales are final, we do not offer any refunds or returns.
                  Please{" "}
                  <ExternalLink href="https://contact.pepsico.com/pantryshop">
                    contact us
                  </ExternalLink>{" "}
                  to let us know if you have any issues or concerns with your
                  Pantry Shop order.
                </li>
              </ul>
            </div>
          </div>
          {/* TODO: Do we need nutrition data? */}
        </div>
      </div>
      {/* Here we have a manually created type as RecommendedProduct and have as RecommendedProduct */}
      {/* because in the auto generated type; it might be undefined as an item in the array. **/}
      <Recommended
        products={product?.recommendedItems || []}
        key={`recommended_${product?._id}`}
      />
    </div>
  );
};

export default ProductPage;
